import React, { CSSProperties, ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canAccessRoute, routes } from '../../../App.routing';
import { RouteType } from '../../../App.enums';
import { useCurrentUser, useIsExternalUser, usePreviewFeatures, useRoleOverride } from '../../../App.hooks';
import { useTheme } from '../../../contexts/ThemeContext';
import ProfileDialog from '../../dialogs/ProfileDialog/ProfileDialog';
import clsx from 'clsx';
import { appVersion, appVersionLabel } from '../../../version';
import {
    LayoutDashboard,
    Users,
    FolderKanban,
    Tags,
    Settings,
    Shield,
    BarChart3,
    ChevronLeft,
    ChevronRight,
    Sun,
    Moon,
    LogOut,
    Menu,
    Eye,
    EyeOff,
    Search,
} from 'lucide-react';

const iconMap: Record<string, React.ComponentType<any>> = {
    'Dashboard': LayoutDashboard,
    'Content Search': Search,
    'Clients': Users,
    'Projects': FolderKanban,
    'Classifications': Tags,
    'Reports': BarChart3,
    'Settings': Settings,
    'Access Control': Shield,
};

type SidebarProps = {
    collapsed: boolean;
    onToggle: () => void;
    style?: CSSProperties;
    children?: ReactNode;
};

const Sidebar = (props: SidebarProps) => {
    const { collapsed, onToggle } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const currentUser = useCurrentUser();
    const isExternalUser = useIsExternalUser();
    const { theme, toggleTheme } = useTheme();
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const [profileDialogVisible, setProfileDialogVisible] = useState(false);
    const { isRealAdmin, isOverrideActive, toggleOverride } = useRoleOverride();
    const { hasFeature } = usePreviewFeatures();

    const visibleRoutes = routes.filter(route => {
        if (route.type !== RouteType.Main) return false;
        return canAccessRoute(route, currentUser, isExternalUser, hasFeature);
    });

    const isActive = (routePath: string) => {
        return location.pathname.toLowerCase().split("/")[1] === routePath.toLowerCase().split("/")[1];
    };

    return (
        <aside
            className={clsx(
                'h-full flex flex-col transition-all duration-300 ease-in-out border-r relative flex-shrink-0',
            )}
            style={{
                width: collapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
                backgroundColor: 'var(--bg-sidebar)',
                borderColor: 'var(--border-default)',
            }}
        >
            {/* Logo Area */}
            <div className={clsx(
                'flex items-center h-16 px-5 border-b flex-shrink-0',
                collapsed && 'justify-center px-0'
            )}
                style={{ borderColor: 'var(--border-default)' }}
            >
                {!collapsed ? (
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center">
                            <span className="text-brand-900 font-bold text-sm">C</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                Content Hub
                            </span>
                            <span className="text-xxs" style={{ color: 'var(--text-tertiary)' }}>
                                Management System
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-lg bg-brand-500 flex items-center justify-center">
                        <span className="text-brand-900 font-bold text-sm">C</span>
                    </div>
                )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                {!collapsed && (
                    <div className="px-3 mb-3">
                        <span className="text-xxs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>
                            Navigation
                        </span>
                    </div>
                )}
                {visibleRoutes.map(route => {
                    const IconComponent = iconMap[route.name] || LayoutDashboard;
                    const active = isActive(route.path);
                    return (
                        <button
                            key={route.path}
                            onClick={() => navigate(route.path)}
                            onMouseEnter={() => setHoveredItem(route.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                            className={clsx(
                                'w-full flex items-center gap-3 rounded-lg transition-all duration-150 relative group',
                                collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                                active ? 'font-medium' : '',
                            )}
                            style={{
                                backgroundColor: active ? 'var(--bg-active)' : 'transparent',
                                color: active ? 'var(--accent-text)' : 'var(--text-secondary)',
                                border: 'none',
                                cursor: 'pointer',
                                outline: 'none',
                            }}
                            onMouseOver={(e) => {
                                if (!active) {
                                    e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                    e.currentTarget.style.color = 'var(--text-primary)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!active) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                }
                            }}
                        >
                            {active && (
                                <div
                                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                                    style={{ backgroundColor: 'var(--accent)' }}
                                />
                            )}
                            <IconComponent size={20} strokeWidth={active ? 2 : 1.5} />
                            {!collapsed && (
                                <span className="text-sm">{route.name}</span>
                            )}
                            {!collapsed && route.badge && (
                                <span
                                    className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                                    style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)', letterSpacing: '0.04em', lineHeight: 1 }}
                                >
                                    {route.badge}
                                </span>
                            )}
                            {/* Tooltip for collapsed mode */}
                            {collapsed && hoveredItem === route.name && (
                                <div
                                    className="tooltip"
                                    style={{ left: 'calc(100% + 12px)', top: '50%', transform: 'translateY(-50%)' }}
                                >
                                    {route.name}
                                </div>
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Bottom Actions */}
            <div className="py-3 px-3 space-y-1 border-t" style={{ borderColor: 'var(--border-default)' }}>
                {/* View as User Toggle (Admin only, gated by env var) */}
                {isRealAdmin && process.env.REACT_APP_ENABLE_VIEW_AS_USER === 'true' && (
                    <button
                        onClick={toggleOverride}
                        title={isOverrideActive ? 'Switch back to Admin view' : 'View as regular user'}
                        className={clsx(
                            'w-full flex items-center gap-3 rounded-lg transition-all duration-150',
                            collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                        )}
                        style={{
                            backgroundColor: isOverrideActive ? 'var(--bg-active)' : 'transparent',
                            color: isOverrideActive ? 'var(--accent-text)' : 'var(--text-secondary)',
                            border: 'none',
                            cursor: 'pointer',
                            outline: 'none',
                        }}
                        onMouseOver={(e) => {
                            if (!isOverrideActive) {
                                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                e.currentTarget.style.color = 'var(--text-primary)';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isOverrideActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'var(--text-secondary)';
                            }
                        }}
                    >
                        {isOverrideActive ? <EyeOff size={20} strokeWidth={1.5} /> : <Eye size={20} strokeWidth={1.5} />}
                        {!collapsed && <span className="text-sm">{isOverrideActive ? 'Back to Admin' : 'View as User'}</span>}
                    </button>
                )}

                {/* Theme Toggle */}
                <button
                    onClick={toggleTheme}
                    className={clsx(
                        'w-full flex items-center gap-3 rounded-lg transition-all duration-150',
                        collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                    )}
                    style={{
                        backgroundColor: 'transparent',
                        color: 'var(--text-secondary)',
                        border: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                >
                    {theme === 'dark' ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
                    {!collapsed && <span className="text-sm">{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
                </button>

                {/* Collapse Toggle */}
                <button
                    onClick={onToggle}
                    className={clsx(
                        'w-full flex items-center gap-3 rounded-lg transition-all duration-150',
                        collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5',
                    )}
                    style={{
                        backgroundColor: 'transparent',
                        color: 'var(--text-secondary)',
                        border: 'none',
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                >
                    {collapsed ? <ChevronRight size={20} strokeWidth={1.5} /> : <ChevronLeft size={20} strokeWidth={1.5} />}
                    {!collapsed && <span className="text-sm">Collapse</span>}
                </button>

                {/* User Info */}
                {currentUser && (
                    <button
                        onClick={() => setProfileDialogVisible(true)}
                        title={collapsed ? `${currentUser?.name} · ${appVersionLabel}` : undefined}
                        className={clsx(
                            'flex items-center gap-3 rounded-lg mt-2 pt-3 border-t w-full transition-all duration-150',
                            collapsed ? 'justify-center px-0' : 'px-3',
                        )}
                        style={{
                            borderColor: 'var(--border-default)',
                            background: 'transparent',
                            border: 'none',
                            borderTop: '1px solid var(--border-default)',
                            cursor: 'pointer',
                            outline: 'none',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                    >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--surface-200)' }}>
                            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {currentUser?.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                        </div>
                        {!collapsed && (
                            <div className="flex flex-col min-w-0 text-left flex-1">
                                <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                    {currentUser?.name}
                                </span>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-xxs truncate" style={{ color: 'var(--text-tertiary)' }}>
                                        {currentUser?.role ?? 'No role'}
                                    </span>
                                    <span
                                        className="text-[9px] px-1.5 py-0.5 rounded-full leading-none flex-shrink-0"
                                        style={{ backgroundColor: 'var(--bg-active)', color: 'var(--text-tertiary)' }}
                                        title={appVersionLabel}
                                    >
                                        v{appVersion}
                                    </span>
                                </div>
                            </div>
                        )}
                    </button>
                )}
            </div>

            {/* Profile Dialog */}
            <ProfileDialog
                visible={profileDialogVisible}
                onClose={() => setProfileDialogVisible(false)}
            />
        </aside>
    );
};

export default Sidebar;
